import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, ShoppingBag } from 'lucide-react';
import { apiRequests } from '../../shared/api/apiRequests';
import { dateToString } from '../../shared/utils/parseDate.js';
import { ORDER_FILTERS, ORDER_STATUS_MAP } from './constants';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';
import './Orders.css';

const FILTER_TABS = [
  { key: null, label: 'Все' },
  { key: 'unpaid', label: 'Не оплачены' },
  { key: 'paid_for', label: 'Оплачены' },
  { key: 'in_delivery', label: 'В доставке' },
  { key: 'delivered', label: 'Доставлены' },
];

const PAGE_SIZE = 10;

const OrdersPage = () => {
  const [data, setData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [searchValue, setSearchValue] = useState(q);
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);

  const activeFilter = searchParams.get('type');
  const typeOfOrder = ORDER_FILTERS[activeFilter];

  // Черновик в поле поиска следует за URL (назад/вперёд в браузере)
  useEffect(() => {
    setSearchValue(q);
  }, [q]);

  // GetOrders принимает и status, и query — без статуса поиск тоже должен работать
  useEffect(() => {
    let cancelled = false;
    apiRequests.products
      .getOrdersFiltered(typeOfOrder, q || undefined, undefined, pageSize, offset)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => console.error(e));
    return () => {
      cancelled = true;
    };
  }, [typeOfOrder, q, offset, pageSize]);

  const updateParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      params.delete('page');
      return params;
    });
  };

  const handleSearch = () => updateParams({ q: searchValue.trim() });

  const handleFilterChange = (filterKey) => updateParams({ type: filterKey });

  return (
    <div className="orders-page">
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск заказов..."
      />

      <section className="orders-filters-shell">
        <div className="orders-filters-label">
          <Filter size={14} />
          <span>Фильтры</span>
        </div>

        <div className="orders-filters-tabs">
          {FILTER_TABS.map((tab) => {
            const isActive = (tab.key === null && !activeFilter) || tab.key === activeFilter;

            return (
              <button
                key={tab.label}
                type="button"
                className={`orders-filter-btn ${isActive ? 'active' : ''}`}
                onClick={() => handleFilterChange(tab.key)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </section>

      <div className="orders-results">
        {!data ? null : !data.items?.length ? (
          q || activeFilter ? (
            <EmptyState
              icon={ShoppingBag}
              variant="search"
              title="Таких заказов нет"
              description={
                activeFilter
                  ? 'В этом статусе нет заказов, подходящих под запрос. Переключитесь на «Все» или измените поиск.'
                  : 'Поиск идёт по названиям товаров в заказе. Проверьте написание или попробуйте часть названия.'
              }
            />
          ) : (
            <EmptyState
              icon={ShoppingBag}
              title="Заказов пока нет"
              description="Как только покупатель оформит заказ в магазине, он появится здесь вместе со статусом оплаты и доставки."
            />
          )
        ) : (
          data.items.map((item) => {
            const status = ORDER_STATUS_MAP[item.status] || { text: 'Новый', variant: 'warning' };

            return (
              <EntityCard
                key={item.id}
                className="orders-card"
                title={`#${item.id}`}
                subtitle={
                  item.user ? `${item.user.name} ${item.user.surname}` : 'Неизвестный пользователь'
                }
                badge={{ text: status.text, variant: status.variant }}
                fields={[
                  {
                    label: 'Сумма',
                    value: `${item.totalSum || 0} ${item.currency?.symbol || 'USD'}`,
                  },
                  { label: 'Создано', value: dateToString(item.createdAt) },
                ]}
                editLink={`/shop/orders/${item.id}`}
              />
            );
          })
        )}
      </div>

      <Pagination page={page} total={data?.totalCount} pageSize={pageSize} onPageChange={setPage} />
    </div>
  );
};

export default OrdersPage;
